import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

interface ProductField {
    id: string;
    field_key: string;
    field_label: string;
    field_type: string;
    required: boolean;
}

function ProductFieldsIndex() {
    const [fields, setFields] = useState<ProductField[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadFields();
    }, []);

    const loadFields = async () => {
        try {
            setLoading(true);
            // Assuming we add this to api.js or use generic get
            const response = await api.get('/settings/product_fields');
            if (response.success) {
                // The API returns { data: [...] } OR direct array in some cases
                setFields(Array.isArray(response.data) ? response.data : (response.data.fields || []));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this field?')) return;

        try {
            const response = await api.delete(`/settings/product_fields/${id}`);
            if (response.success) {
                setFields(fields.filter(field => field.id !== id));
            } else {
                alert('Failed to delete field');
            }
        } catch (err) {
            console.error(err);
            alert('Error deleting field');
        }
    };

    if (loading) return <div className="mt-16 text-center text-slate-500">Loading custom fields...</div>;

    return (
        <div className="mt-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Custom Product Fields</h1>
                    <p className="text-slate-500 mt-1">Define custom attributes for your products</p>
                </div>
                <Link
                    to="/settings/product-fields/new"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Field</span>
                </Link>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {fields.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                        {fields.map((field) => (
                            <div key={field.id} className="p-6 flex flex-col sm:flex-row sm:items-center gap-6">
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="flex flex-col gap-1">
                                        <button className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>
                                        </button>
                                        <button className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-slate-800 text-lg">{field.field_label}</p>
                                        <p className="text-sm text-slate-500 font-mono tracking-tight">{field.field_key}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-600 uppercase tracking-wider">
                                        {field.field_type}
                                    </span>
                                    {field.required && (
                                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-600 uppercase tracking-wider">Required</span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 sm:ml-6">
                                    <Link
                                        to={`/settings/product-fields/${field.id}/edit`}
                                        className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(field.id)}
                                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                                        title="Delete Field"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-20 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No custom fields yet</h3>
                        <p className="text-slate-500 max-w-sm mx-auto">Add fields like Engine CC, IMEI, Color, or any other attribute your products need.</p>
                        <Link
                            to="/settings/product-fields/new"
                            className="inline-flex items-center gap-2 mt-8 text-indigo-600 font-bold hover:text-indigo-700 transition-colors"
                        >
                            Create your first field
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductFieldsIndex;
